// ProjectTicketTab.jsx - Updated version
import React, { useState, useEffect } from "react";
import { Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import SearchableMultiSelect from "./SearchableMultiSelect";
import "./ProjectTicketTab.css";

const ProjectTicketTab = ({
  // Data
  filteredTickets,
  ticketsLoading,
  assignmentsByTicket,
  teamMemberDetails,
  resourcesByOrg,
  cusVenCpCache,
  resolvedTicketNames,
  statusMasterData,

  // Handlers
  openTicketDialog,
  handleEditTicket,
  handleConvertToTask,
  getStatusBadgeWithName,
  resolveSubStatusName,
  getPriorityBadge,
  resolveContactName,
  formatDateTime,
  formatDate,

  // Search/Filter State - Updated for multi-select
  searchText,
  setSearchText,
  statusFilter, // Now an array of status GUIDs
  setStatusFilter,
  priorityFilter, // Now an array
  setPriorityFilter,
  typeFilter, // New: array of type GUIDs
  setTypeFilter,
  assigneeFilter, // New: array of assignee GUIDs/IDs
  setAssigneeFilter,

  // Navigation
  navigate,
  projectId,
}) => {
  // Generate filter options from API data
  const getStatusOptions = () => {
    if (!statusMasterData?.allStatuses) return [];
    
    const options = [];
    statusMasterData.allStatuses.forEach((status) => {
      options.push({
        id: status.statusGuid,
        name: status.statusName,
      });
      // Add sub-statuses as separate options
      if (status.subStatuses && status.subStatuses.length > 0) {
        status.subStatuses.forEach((subStatus) => {
          options.push({
            id: subStatus.statusGuid,
            name: `${status.statusName} - ${subStatus.statusName}`,
            parentStatus: status.statusGuid,
          });
        });
      }
    });
    return options;
  };

  const getPriorityOptions = () => {
    return [
      { id: "Critical", name: "Critical" },
      { id: "High", name: "High" },
      { id: "Medium", name: "Medium" },
      { id: "Low", name: "Low" },
    ];
  };

  const getTypeOptions = () => {
    if (!resolvedTicketNames?.types) return [];
    return Object.entries(resolvedTicketNames.types).map(([guid, name]) => ({
      id: guid,
      name: name,
    }));
  };

  const getAssigneeOptions = () => {
    const options = [];
    const seen = new Set();

    // Add team members
    teamMemberDetails.forEach((member) => {
      if (!seen.has(member.userId)) {
        seen.add(member.userId);
        options.push({
          id: member.userId,
          name: member.username,
          type: "team",
        });
      }
    });

    // Add resources
    resourcesByOrg.forEach((resource) => {
      if (!seen.has(resource.resourceId)) {
        seen.add(resource.resourceId);
        options.push({
          id: resource.resourceId,
          name: resource.name,
          type: "resource",
        });
      }
    });

    // Sort alphabetically
    options.sort((a, b) => a.name.localeCompare(b.name));
    return options;
  };

  // Helper to check if a ticket matches the filters
  const isTicketMatched = (ticket) => {
    // Status filter (check if ticket status matches any selected status GUID)
    if (statusFilter.length > 0) {
      const ticketStatusGuid = ticket.statusGuid || ticket.status;
      if (!statusFilter.includes(ticketStatusGuid)) {
        return false;
      }
    }

    // Priority filter
    if (priorityFilter.length > 0) {
      const ticketPriority = ticket.priority || "Medium";
      if (!priorityFilter.includes(ticketPriority)) {
        return false;
      }
    }

    // Type filter
    if (typeFilter.length > 0) {
      const ticketType = ticket.ticketType;
      if (!typeFilter.includes(ticketType)) {
        return false;
      }
    }

    // Assignee filter
    if (assigneeFilter.length > 0) {
      const assignedTo = ticket.assignedTo ? ticket.assignedTo.split(",") : [];
      const hasMatch = assignedTo.some(assignee => assigneeFilter.includes(String(assignee).trim()));
      if (!hasMatch) {
        return false;
      }
    }

    return true;
  };

  // Apply all filters
  const getFilteredAndSearchedTickets = () => {
    let filtered = [...filteredTickets];

    // Apply search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((ticket) =>
        ticket.ticketName?.toLowerCase().includes(searchLower) ||
        ticket.ticketShortId?.toLowerCase().includes(searchLower) ||
        ticket.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply multi-select filters
    filtered = filtered.filter(isTicketMatched);

    return filtered;
  };

  const finalFilteredTickets = getFilteredAndSearchedTickets();

  // Clear all filters
  const clearAllFilters = () => {
    setSearchText("");
    setStatusFilter([]);
    setPriorityFilter([]);
    setTypeFilter([]);
    setAssigneeFilter([]);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return (
      searchText.trim() !== "" ||
      statusFilter.length > 0 ||
      priorityFilter.length > 0 ||
      typeFilter.length > 0 ||
      assigneeFilter.length > 0
    );
  };

  return (
    <div className="ptt-ticket-container">
      <div className="ptt-main-card">
        {/* <div className="ptt-header">
          <div className="ptt-header-left">
            <div className="ptt-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 8h8v2H8V8zm0 4h8v2H8v-2z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h3 className="ptt-title">Project Tickets</h3>
              <p className="ptt-subtitle">Manage and track all project tickets</p>
            </div>
          </div>
        </div> */}

        {/* Enhanced Filters Section */}
        <div className="ptt-filters-section">
          <div className="ptt-filters-wrapper">
            {/* Search Box */}
            <div className="ptt-search-box">
              <svg className="ptt-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
              </svg>
              <input
                type="text"
                className="ptt-search-input"
                placeholder="Search by ticket name, ID, or description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* Multi-select Filters Row */}
            <div className="ptt-multi-filters-row">
              <div className="ptt-filter-item">
                <label className="ptt-filter-label">Status</label>
                <SearchableMultiSelect
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={getStatusOptions()}
                  placeholder="All Status"
                  searchPlaceholder="Search status..."
                  optionValueKey="id"
                  optionLabelKey="name"
                  sortOptions={true}
                />
              </div>

              <div className="ptt-filter-item">
                <label className="ptt-filter-label">Priority</label>
                <SearchableMultiSelect
                  value={priorityFilter}
                  onChange={setPriorityFilter}
                  options={getPriorityOptions()}
                  placeholder="All Priority"
                  searchPlaceholder="Search priority..."
                  optionValueKey="id"
                  optionLabelKey="name"
                  sortOptions={true}
                />
              </div>

              <div className="ptt-filter-item">
                <label className="ptt-filter-label">Type</label>
                <SearchableMultiSelect
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={getTypeOptions()}
                  placeholder="All Types"
                  searchPlaceholder="Search types..."
                  optionValueKey="id"
                  optionLabelKey="name"
                  sortOptions={true}
                />
              </div>

              <div className="ptt-filter-item">
                <label className="ptt-filter-label">Assignee</label>
                <SearchableMultiSelect
                  value={assigneeFilter}
                  onChange={setAssigneeFilter}
                  options={getAssigneeOptions()}
                  placeholder="All Assignees"
                  searchPlaceholder="Search assignees..."
                  optionValueKey="id"
                  optionLabelKey="name"
                  sortOptions={true}
                />
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters() && (
              <div className="ptt-active-filters">
                <span className="ptt-active-filters-label">Active Filters:</span>
                <div className="ptt-filter-tags">
                  {searchText && (
                    <span className="ptt-filter-tag">
                      Search: {searchText}
                      <button onClick={() => setSearchText("")}>×</button>
                    </span>
                  )}
                  {statusFilter.map((statusId) => {
                    const status = getStatusOptions().find(s => s.id === statusId);
                    return status ? (
                      <span key={statusId} className="ptt-filter-tag">
                        Status: {status.name}
                        <button onClick={() => setStatusFilter(statusFilter.filter(s => s !== statusId))}>×</button>
                      </span>
                    ) : null;
                  })}
                  {priorityFilter.map((priority) => (
                    <span key={priority} className="ptt-filter-tag">
                      Priority: {priority}
                      <button onClick={() => setPriorityFilter(priorityFilter.filter(p => p !== priority))}>×</button>
                    </span>
                  ))}
                  {typeFilter.map((typeId) => {
                    const type = getTypeOptions().find(t => t.id === typeId);
                    return type ? (
                      <span key={typeId} className="ptt-filter-tag">
                        Type: {type.name}
                        <button onClick={() => setTypeFilter(typeFilter.filter(t => t !== typeId))}>×</button>
                      </span>
                    ) : null;
                  })}
                  {assigneeFilter.map((assigneeId) => {
                    const assignee = getAssigneeOptions().find(a => String(a.id) === String(assigneeId));
                    return assignee ? (
                      <span key={assigneeId} className="ptt-filter-tag">
                        Assignee: {assignee.name}
                        <button onClick={() => setAssigneeFilter(assigneeFilter.filter(a => String(a) !== String(assigneeId)))}>×</button>
                      </span>
                    ) : null;
                  })}
                  <button className="ptt-clear-all-filters" onClick={clearAllFilters}>
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="ptt-results-count">
          Showing {finalFilteredTickets.length} of {filteredTickets.length} tickets
        </div>

        {/* Tickets Content */}
        {ticketsLoading ? (
          <div className="ptt-loading-state">
            <div className="ptt-loading-spinner"></div>
            <p>Loading tickets...</p>
          </div>
        ) : (
          <div className="ptt-tickets-content">
            {finalFilteredTickets.length === 0 ? (
              <div className="ptt-empty-state">
                <div className="ptt-empty-icon">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 8h8v2H8V8zm0 4h8v2H8v-2z" fill="currentColor"/>
                  </svg>
                </div>
                <h5 className="ptt-empty-title">No Tickets Found</h5>
                <p className="ptt-empty-message">
                  {hasActiveFilters() 
                    ? "No tickets match your current filters. Try adjusting your criteria."
                    : "There are no tickets associated with this project."}
                </p>
                {hasActiveFilters() && (
                  <button className="ptt-empty-create-btn" onClick={clearAllFilters}>
                    Clear All Filters
                  </button>
                )}
                <button
                  className="ptt-empty-create-btn"
                  onClick={() => navigate(`/addTicket?projectId=${projectId}`)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                  </svg>
                  Create Your First Ticket
                </button>
              </div>
            ) : (
              <div className="ptt-table-wrapper">
                <table className="ptt-ticket-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Short ID</th>
                      <th>Ticket Name</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Type</th>
                      <th>Submitted By</th>
                      <th>Created Date</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalFilteredTickets.map((ticket, index) => (
                      <tr key={ticket.ticketId} className="ptt-table-row" style={{ animationDelay: `${index * 0.03}s` }}>
                        <td className="ptt-cell-id">
                          <span className="ptt-id-badge">{ticket.ticketId}</span>
                        </td>
                        <td className="ptt-cell-short-id">
                          <span className="ptt-short-id">{ticket.ticketShortId}</span>
                        </td>
                        <td className="ptt-cell-name">
                          <Link
                            to={`/ticketinfo/${ticket.ticketId}`}
                            target="_blank"
                            className="ptt-ticket-link"
                          >
                            {ticket.ticketName}
                          </Link>
                          {ticket.description && (
                            <div className="ptt-ticket-description">
                              {ticket.description.length > 60
                                ? `${ticket.description.substring(0, 60)}...`
                                : ticket.description}
                            </div>
                          )}
                        </td>
                        <td className="ptt-cell-status">
                          {getStatusBadgeWithName(ticket.status)}
                        </td>
                        <td className="ptt-cell-priority">
                          <span className={`ptt-priority-badge ptt-priority-${ticket.priority?.toLowerCase()}`}>
                            {ticket.priority || "Medium"}
                          </span>
                        </td>
                        <td className="ptt-cell-type">
                          <span className="ptt-type-badge">
                            {resolvedTicketNames.types[ticket.ticketType?.toLowerCase()] ||
                              ticket.ticketType?.substring(0, 8) ||
                              "N/A"}
                          </span>
                        </td>
                        <td className="ptt-cell-submitted">
                          <div className="ptt-user-info">
                            <div className="ptt-user-avatar">
                              {resolveContactName(ticket.submittedBy || ticket.reportedBy || "-").charAt(0)}
                            </div>
                            <span>{resolveContactName(ticket.submittedBy || ticket.reportedBy || "-")}</span>
                          </div>
                        </td>
                        <td className="ptt-cell-date">
                          {formatDateTime(ticket.ticketTime)}
                        </td>
                        <td className="ptt-cell-date">
                          {formatDate(ticket.dueDate)}
                        </td>
                        <td className="ptt-cell-actions">
                          <div className="ptt-action-buttons">
                            {/* <button
                              className="ptt-action-btn ptt-view-btn"
                              onClick={() => openTicketDialog(ticket)}
                              title="View Details"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                              </svg>
                            </button> */}
                            <button
                              className="ptt-action-btn ptt-edit-btn"
                              onClick={() => handleEditTicket(ticket)}
                              title="Edit Ticket"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                              </svg>
                            </button>
                            <button
                              className="ptt-action-btn ptt-convert-btn"
                              onClick={() => handleConvertToTask(ticket)}
                              title="Convert to Task"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTicketTab;

// import React from "react";
// import { Button, Badge, Form } from "react-bootstrap";
// import { Link } from "react-router-dom"; // Add this import
// import moment from "moment";

// const ProjectTicketTab = ({
//   // Data
//   filteredTickets,
//   ticketsLoading,
//   assignmentsByTicket,
//   teamMemberDetails,
//   resourcesByOrg,
//   cusVenCpCache,
//   resolvedTicketNames,
//   statusMasterData,

//   // Handlers
//   openTicketDialog,
//   handleEditTicket,
//   handleConvertToTask,
//   getStatusBadgeWithName,
//   resolveSubStatusName,
//   getPriorityBadge,
//   resolveContactName,
//   formatDateTime,
//   formatDate,

//   // Search/Filter State
//   searchText,
//   setSearchText,
//   statusFilter,
//   setStatusFilter,
//   priorityFilter,
//   setPriorityFilter,

//   // Navigation
//   navigate,
//   projectId,
// }) => {
//   // Helper function to resolve user name from various sources
//   const resolveUserName = (userId) => {
//     if (!userId) return "-";

//     // Check if it's a contact person (CVCP prefix)
//     if (typeof userId === "string" && userId.startsWith("CVCP")) {
//       return cusVenCpCache[userId] || userId;
//     }

//     // Check in team members
//     const teamMember = teamMemberDetails.find(
//       (user) => String(user.userId) === String(userId),
//     );
//     if (teamMember) return teamMember.username;

//     // Check in resources by org
//     const resource = resourcesByOrg.find(
//       (res) => String(res.resourceId) === String(userId),
//     );
//     if (resource) return resource.name;

//     return userId;
//   };

//   return (
//     <div className="project-view-fade-in">
//       <div className="project-view-ticket-list-container">
//         <div className="ticket-list-header">
//           <h4 className="ticket-list-title">Project Tickets</h4>
//           <div
//             className="ticket-list-filters d-flex gap-2 flex-wrap"
//             style={{
//               display: "flex",
//               flexDirection: "row",
//               width: "50vw",
//             }}
//           >
//             <span>
//               <input
//                 type="text"
//                 className="form-control"
//                 placeholder="Search ticket name or description..."
//                 style={{ maxWidth: "250px" }}
//                 value={searchText}
//                 onChange={(e) => setSearchText(e.target.value)}
//               />
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={statusFilter}
//                 onChange={(e) => setStatusFilter(e.target.value)}
//               >
//                 <option value="">All Status</option>
//                 <option value="Open">Open</option>
//                 <option value="In Progress">In Progress</option>
//                 <option value="Resolved">Resolved</option>
//                 <option value="Closed">Closed</option>
//               </select>
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={priorityFilter}
//                 onChange={(e) => setPriorityFilter(e.target.value)}
//               >
//                 <option value="">All Priority</option>
//                 <option value="Low">Low</option>
//                 <option value="Medium">Medium</option>
//                 <option value="High">High</option>
//                 <option value="Critical">Critical</option>
//               </select>
//             </span>

//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => {
//                 setSearchText("");
//                 setStatusFilter("");
//                 setPriorityFilter("");
//               }}
//             >
//               Clear
//             </Button>
//           </div>
//         </div>

//         {ticketsLoading ? (
//           <div className="text-center py-4">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading tickets...</span>
//             </div>
//           </div>
//         ) : (
//           <div className="ticket-table-container">
//             {filteredTickets.length === 0 ? (
//               <div className="text-center py-5 text-muted">
//                 <i className="fas fa-ticket-alt fa-3x mb-3"></i>
//                 <h5>No Tickets Found</h5>
//                 <p className="mb-4">
//                   There are no tickets associated with this project.
//                 </p>
//                 <Button
//                   variant="outline-primary"
//                   size="sm"
//                   onClick={() => navigate(`/addTicket?projectId=${projectId}`)}
//                 >
//                   <i className="fas fa-plus me-2"></i>
//                   Create First Ticket
//                 </Button>
//               </div>
//             ) : (
//               <div className="table-responsive">
//                 <table className="project-view-ticket-table">
//                   <thead className="project-view-ticket-table-header">
//                     <tr>
//                       <th>Ticket ID</th>
//                       <th>Ticket Short ID</th>
//                       <th>Ticket Name</th>
//                       <th>Description</th>
//                       <th>Status</th>
//                       <th>Sub Status</th>
//                       <th>Ticket Type</th>
//                       <th>Priority</th>
//                       <th>Submitted By</th>
//                       <th>Created By</th>
//                       {/* <th>Assigned To</th> */}
//                       <th>Category</th>
//                       <th>Ticket Reason</th>
//                       <th>Created Date</th>
//                       <th>Due Date</th>
//                       <th>Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody className="ticket-table-body">
//                     {filteredTickets.map((ticket) => {
//                       return (
//                         <tr key={ticket.ticketId} className="ticket-table-row">
//                           <td className="ticket-id-cell">
//                             <div className="ticket-id">{ticket.ticketId}</div>
//                           </td>
//                           <td className="ticket-short-id-cell">
//                             <div className="ticket-short-id">
//                               {ticket.ticketShortId}
//                             </div>
//                           </td>
//                           <td className="ticket-name-cell">
//                             {/* Updated: Make ticket name clickable with Link */}
//                             <Link
//                               to={`/ticketinfo/${ticket.ticketId}`}
//                               target="_blank"
//                               rel="noopener noreferrer"
//                               className="ticket-name-link"
//                               style={{
//                                 color: "#0d6efd",
//                                 textDecoration: "none",
//                                 fontWeight: "500",
//                                 cursor: "pointer",
//                               }}
//                               onMouseEnter={(e) => {
//                                 e.currentTarget.style.textDecoration =
//                                   "underline";
//                               }}
//                               onMouseLeave={(e) => {
//                                 e.currentTarget.style.textDecoration = "none";
//                               }}
//                               title="Open ticket details (opens in new tab)"
//                             >
//                               {ticket.ticketName}
//                             </Link>
//                           </td>
//                           <td className="ticket-description-cell">
//                             <div className="ticket-description">
//                               {ticket.description
//                                 ? ticket.description.length > 100
//                                   ? `${ticket.description.substring(0, 100)}...`
//                                   : ticket.description
//                                 : "-"}
//                             </div>
//                           </td>
//                           <td className="ticket-status-cell">
//                             {getStatusBadgeWithName(ticket.status)}
//                           </td>
//                           <td className="ticket-substatus-cell">
//                             <Badge bg="secondary">
//                               {resolveSubStatusName(ticket.subStatus)}
//                             </Badge>
//                           </td>
//                           <td className="ticket-type-cell">
//                             <Badge bg="outline-info" style={{ color: "black" }}>
//                               {resolvedTicketNames.types[
//                                 ticket.ticketType?.toLowerCase()
//                               ] ||
//                                 ticket.ticketType?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-priority-cell">
//                             <span
//                               className={`project-view-ticket-priority-badge ${getPriorityBadge(
//                                 ticket.priority,
//                               )}`}
//                             >
//                               {ticket.priority}
//                             </span>
//                           </td>

//                           {/* Submitted By Column */}
//                           <td className="ticket-submittedby-cell">
//                             <div className="ticket-submittedby">
//                               {resolveContactName(
//                                 ticket.submittedBy ||
//                                   ticket.reportedBy ||
//                                   ticket.customerId ||
//                                   "-",
//                               )}
//                             </div>
//                           </td>

//                           {/* Created By Column */}
//                           <td className="ticket-createdby-cell">
//                             <div className="ticket-createdby">
//                               {resolveUserName(ticket.createdBy)}
//                             </div>
//                           </td>

//                           {/* Assigned To Column */}
//                           {/* <td className="ticket-assignedto-cell">
//                             <div className="ticket-assignedto d-flex flex-column gap-1">
//                               {(() => {
//                                 const currentAssignments =
//                                   assignmentsByTicket[ticket.ticketId] || [];

//                                 if (currentAssignments.length === 0) {
//                                   return (
//                                     <div className="d-flex align-items-center justify-content-between">
//                                       <span className="text-muted">
//                                         Unassigned
//                                       </span>
//                                       <Button
//                                         variant="outline-primary"
//                                         size="sm"
//                                         onClick={() => openTicketDialog(ticket)}
//                                         className="ms-2"
//                                         title="Assign user"
//                                       >
//                                         Assign
//                                       </Button>
//                                     </div>
//                                   );
//                                 }

//                                 return (
//                                   <>
//                                     {currentAssignments.map(
//                                       (assignment, index) => {
//                                         const userName = resolveUserName(
//                                           assignment.assignedUserId,
//                                         );

//                                         return (
//                                           <div
//                                             key={
//                                               assignment.assignmentId || index
//                                             }
//                                             className="d-flex align-items-center justify-content-between"
//                                           >
//                                             <div className="d-flex align-items-center">
//                                               <span className="me-2">
//                                                 {userName}
//                                               </span>
//                                               <Badge
//                                                 bg={
//                                                   assignment.isActive
//                                                     ? "success"
//                                                     : "secondary"
//                                                 }
//                                                 className="assignment-status-badge"
//                                               >
//                                                 {assignment.isActive
//                                                   ? "Active"
//                                                   : "Completed"}
//                                               </Badge>
//                                               {assignment.totalHours && (
//                                                 <small className="ms-2 text-muted">
//                                                   (
//                                                   {parseFloat(
//                                                     assignment.totalHours,
//                                                   ).toFixed(2)}
//                                                   h)
//                                                 </small>
//                                               )}
//                                             </div>

//                                             {index === 0 && (
//                                               <Button
//                                                 variant="outline-primary"
//                                                 size="sm"
//                                                 onClick={() =>
//                                                   openTicketDialog(ticket)
//                                                 }
//                                                 className="ms-2"
//                                                 title="Manage Assignments"
//                                               >
//                                                 Manage
//                                               </Button>
//                                             )}
//                                           </div>
//                                         );
//                                       },
//                                     )}

//                                     {currentAssignments.length > 2 && (
//                                       <small className="text-muted">
//                                         +{currentAssignments.length - 2} more
//                                         assignments
//                                       </small>
//                                     )}
//                                   </>
//                                 );
//                               })()}
//                             </div>
//                           </td> */}

//                           <td className="ticket-category-cell">
//                             <Badge bg="info">
//                               {resolvedTicketNames.categories[
//                                 ticket.category?.toLowerCase()
//                               ] ||
//                                 ticket.category?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-reason-cell">
//                             <div className="ticket-reason">
//                               {resolvedTicketNames.reasons[
//                                 ticket.ticketReason?.toLowerCase()
//                               ] ||
//                                 ticket.ticketReason ||
//                                 "N/A"}
//                             </div>
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDateTime(ticket.ticketTime)}
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDate(ticket.dueDate)}
//                           </td>
//                           <td className="ticket-actions-cell">
//                             <div
//                               className="ticket-actions"
//                               style={{ display: "flex" }}
//                             >
//                               <Button
//                                 variant="outline-secondary"
//                                 size="sm"
//                                 className="me-1"
//                                 onClick={() => openTicketDialog(ticket)}
//                                 title="Open Ticket Details"
//                               >
//                                 Open
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleEditTicket(ticket)}
//                                 title="Edit Ticket"
//                                 className="me-1"
//                               >
//                                 Edit
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleConvertToTask(ticket)}
//                                 title="Convert to Task"
//                                 className="me-1"
//                               >
//                                 Convert to Task
//                               </Button>
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectTicketTab;

// import React from "react";
// import { Button, Badge, Form } from "react-bootstrap";
// import moment from "moment";

// const ProjectTicketTab = ({
//   // Data
//   filteredTickets,
//   ticketsLoading,
//   assignmentsByTicket,
//   teamMemberDetails,
//   resourcesByOrg,
//   cusVenCpCache,
//   resolvedTicketNames,
//   statusMasterData,

//   // Handlers
//   openTicketDialog,
//   handleEditTicket,
//   handleConvertToTask,
//   getStatusBadgeWithName,
//   resolveSubStatusName,
//   getPriorityBadge,
//   resolveContactName,
//   formatDateTime,
//   formatDate,

//   // Search/Filter State
//   searchText,
//   setSearchText,
//   statusFilter,
//   setStatusFilter,
//   priorityFilter,
//   setPriorityFilter,

//   // Navigation
//   navigate,
//   projectId,
// }) => {
//   // Helper function to resolve user name from various sources
//   const resolveUserName = (userId) => {
//     if (!userId) return "-";

//     // Check if it's a contact person (CVCP prefix)
//     if (typeof userId === "string" && userId.startsWith("CVCP")) {
//       return cusVenCpCache[userId] || userId;
//     }

//     // Check in team members
//     const teamMember = teamMemberDetails.find(
//       (user) => String(user.userId) === String(userId),
//     );
//     if (teamMember) return teamMember.username;

//     // Check in resources by org
//     const resource = resourcesByOrg.find(
//       (res) => String(res.resourceId) === String(userId),
//     );
//     if (resource) return resource.name;

//     return userId;
//   };

//   return (
//     <div className="project-view-fade-in">
//       <div className="project-view-ticket-list-container">
//         <div className="ticket-list-header">
//           <h4 className="ticket-list-title">Project Tickets</h4>
//           <div
//             className="ticket-list-filters d-flex gap-2 flex-wrap"
//             style={{
//               display: "flex",
//               flexDirection: "row",
//               width: "50vw",
//             }}
//           >
//             <span>
//               <input
//                 type="text"
//                 className="form-control"
//                 placeholder="Search ticket name or description..."
//                 style={{ maxWidth: "250px" }}
//                 value={searchText}
//                 onChange={(e) => setSearchText(e.target.value)}
//               />
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={statusFilter}
//                 onChange={(e) => setStatusFilter(e.target.value)}
//               >
//                 <option value="">All Status</option>
//                 <option value="Open">Open</option>
//                 <option value="In Progress">In Progress</option>
//                 <option value="Resolved">Resolved</option>
//                 <option value="Closed">Closed</option>
//               </select>
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={priorityFilter}
//                 onChange={(e) => setPriorityFilter(e.target.value)}
//               >
//                 <option value="">All Priority</option>
//                 <option value="Low">Low</option>
//                 <option value="Medium">Medium</option>
//                 <option value="High">High</option>
//                 <option value="Critical">Critical</option>
//               </select>
//             </span>

//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => {
//                 setSearchText("");
//                 setStatusFilter("");
//                 setPriorityFilter("");
//               }}
//             >
//               Clear
//             </Button>
//           </div>
//         </div>

//         {ticketsLoading ? (
//           <div className="text-center py-4">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading tickets...</span>
//             </div>
//           </div>
//         ) : (
//           <div className="ticket-table-container">
//             {filteredTickets.length === 0 ? (
//               <div className="text-center py-5 text-muted">
//                 <i className="fas fa-ticket-alt fa-3x mb-3"></i>
//                 <h5>No Tickets Found</h5>
//                 <p className="mb-4">
//                   There are no tickets associated with this project.
//                 </p>
//                 <Button
//                   variant="outline-primary"
//                   size="sm"
//                   onClick={() => navigate(`/addTicket?projectId=${projectId}`)}
//                 >
//                   <i className="fas fa-plus me-2"></i>
//                   Create First Ticket
//                 </Button>
//               </div>
//             ) : (
//               <div className="table-responsive">
//                 <table className="project-view-ticket-table">
//                   <thead className="project-view-ticket-table-header">
//                     <tr>
//                       <th>Ticket ID</th>
//                       <th>Ticket Short ID</th>
//                       <th>Ticket Name</th>
//                       <th>Description</th>
//                       <th>Status</th>
//                       <th>Sub Status</th>
//                       <th>Ticket Type</th>
//                       <th>Priority</th>
//                       <th>Submitted By</th>
//                       <th>Created By</th>
//                       <th>Assigned To</th>
//                       <th>Category</th>
//                       <th>Ticket Reason</th>
//                       <th>Created Date</th>
//                       <th>Due Date</th>
//                       <th>Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody className="ticket-table-body">
//                     {filteredTickets.map((ticket) => {
//                       return (
//                         <tr key={ticket.ticketId} className="ticket-table-row">
//                           <td className="ticket-id-cell">
//                             <div className="ticket-id">{ticket.ticketId}</div>
//                           </td>
//                           <td className="ticket-short-id-cell">
//                             <div className="ticket-short-id">
//                               {ticket.ticketShortId}
//                             </div>
//                           </td>
//                           <td className="ticket-name-cell">
//                             <div className="ticket-name">
//                               {ticket.ticketName}
//                             </div>
//                           </td>
//                           <td className="ticket-description-cell">
//                             <div className="ticket-description">
//                               {ticket.description
//                                 ? ticket.description.length > 100
//                                   ? `${ticket.description.substring(0, 100)}...`
//                                   : ticket.description
//                                 : "-"}
//                             </div>
//                           </td>
//                           <td className="ticket-status-cell">
//                             {getStatusBadgeWithName(ticket.status)}
//                           </td>
//                           <td className="ticket-substatus-cell">
//                             <Badge bg="secondary">
//                               {resolveSubStatusName(ticket.subStatus)}
//                             </Badge>
//                           </td>
//                           <td className="ticket-type-cell">
//                             <Badge bg="outline-info" style={{ color: "black" }}>
//                               {resolvedTicketNames.types[
//                                 ticket.ticketType?.toLowerCase()
//                               ] ||
//                                 ticket.ticketType?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-priority-cell">
//                             <span
//                               className={`project-view-ticket-priority-badge ${getPriorityBadge(
//                                 ticket.priority,
//                               )}`}
//                             >
//                               {ticket.priority}
//                             </span>
//                           </td>

//                           {/* Submitted By Column */}
//                           <td className="ticket-submittedby-cell">
//                             <div className="ticket-submittedby">
//                               {resolveContactName(
//                                 ticket.submittedBy ||
//                                   ticket.reportedBy ||
//                                   ticket.customerId ||
//                                   "-",
//                               )}
//                             </div>
//                           </td>

//                           {/* Created By Column */}
//                           <td className="ticket-createdby-cell">
//                             <div className="ticket-createdby">
//                               {resolveUserName(ticket.createdBy)}
//                             </div>
//                           </td>

//                           {/* Assigned To Column */}
//                           <td className="ticket-assignedto-cell">
//                             <div className="ticket-assignedto d-flex flex-column gap-1">
//                               {(() => {
//                                 const currentAssignments =
//                                   assignmentsByTicket[ticket.ticketId] || [];

//                                 if (currentAssignments.length === 0) {
//                                   return (
//                                     <div className="d-flex align-items-center justify-content-between">
//                                       <span className="text-muted">
//                                         Unassigned
//                                       </span>
//                                       <Button
//                                         variant="outline-primary"
//                                         size="sm"
//                                         onClick={() => openTicketDialog(ticket)}
//                                         className="ms-2"
//                                         title="Assign user"
//                                       >
//                                         Assign
//                                       </Button>
//                                     </div>
//                                   );
//                                 }

//                                 return (
//                                   <>
//                                     {currentAssignments.map(
//                                       (assignment, index) => {
//                                         const userName = resolveUserName(
//                                           assignment.assignedUserId,
//                                         );

//                                         return (
//                                           <div
//                                             key={
//                                               assignment.assignmentId || index
//                                             }
//                                             className="d-flex align-items-center justify-content-between"
//                                           >
//                                             <div className="d-flex align-items-center">
//                                               <span className="me-2">
//                                                 {userName}
//                                               </span>
//                                               <Badge
//                                                 bg={
//                                                   assignment.isActive
//                                                     ? "success"
//                                                     : "secondary"
//                                                 }
//                                                 className="assignment-status-badge"
//                                               >
//                                                 {assignment.isActive
//                                                   ? "Active"
//                                                   : "Completed"}
//                                               </Badge>
//                                               {assignment.totalHours && (
//                                                 <small className="ms-2 text-muted">
//                                                   (
//                                                   {parseFloat(
//                                                     assignment.totalHours,
//                                                   ).toFixed(2)}
//                                                   h)
//                                                 </small>
//                                               )}
//                                             </div>

//                                             {index === 0 && (
//                                               <Button
//                                                 variant="outline-primary"
//                                                 size="sm"
//                                                 onClick={() =>
//                                                   openTicketDialog(ticket)
//                                                 }
//                                                 className="ms-2"
//                                                 title="Manage Assignments"
//                                               >
//                                                 Manage
//                                               </Button>
//                                             )}
//                                           </div>
//                                         );
//                                       },
//                                     )}

//                                     {currentAssignments.length > 2 && (
//                                       <small className="text-muted">
//                                         +{currentAssignments.length - 2} more
//                                         assignments
//                                       </small>
//                                     )}
//                                   </>
//                                 );
//                               })()}
//                             </div>
//                           </td>

//                           <td className="ticket-category-cell">
//                             <Badge bg="info">
//                               {resolvedTicketNames.categories[
//                                 ticket.category?.toLowerCase()
//                               ] ||
//                                 ticket.category?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-reason-cell">
//                             <div className="ticket-reason">
//                               {resolvedTicketNames.reasons[
//                                 ticket.ticketReason?.toLowerCase()
//                               ] ||
//                                 ticket.ticketReason ||
//                                 "N/A"}
//                             </div>
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDateTime(ticket.ticketTime)}
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDate(ticket.dueDate)}
//                           </td>
//                           <td className="ticket-actions-cell">
//                             <div
//                               className="ticket-actions"
//                               style={{ display: "flex" }}
//                             >
//                               <Button
//                                 variant="outline-secondary"
//                                 size="sm"
//                                 className="me-1"
//                                 onClick={() => openTicketDialog(ticket)}
//                                 title="Open Ticket Details"
//                               >
//                                 Open
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleEditTicket(ticket)}
//                                 title="Edit Ticket"
//                                 className="me-1"
//                               >
//                                 Edit
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleConvertToTask(ticket)}
//                                 title="Convert to Task"
//                                 className="me-1"
//                               >
//                                 Convert to Task
//                               </Button>
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectTicketTab;
